import { useDispatch, useSelector } from 'react-redux';
import { Filter } from 'common/components/filter/Filter';
import {
  FilterState,
  setPlanValue,
  setModelValue,
  PlanOptionValue
} from 'common/modules/app/filter';
import { AppState } from 'pages';
import { Glossary } from 'common/utils/glossary';
import { GlossaryState } from 'common/modules/app/glossaries';
import { push, RouterState } from 'connected-react-router';
import { SasCookieKeys, saveCookie } from 'common/utils/cookie-utils';
import { getPlanListUrl, getPurchaseUrl } from 'common/utils/urls';
import {
  GET_PLAN_LIST_ID,
  filterState as planFilterState
} from 'common/modules/domain/plan';
import {
  GET_PURCHASE_HISTORY_ID,
  filterState as purchaseFilterState
} from 'common/modules/domain/purchase';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';
import { Principal } from 'common/modules/app/principal';

/** StoreのStateからComponentに渡すProps */
const useStateProps = () => {
  const filterState = useSelector<AppState, FilterState>(
    (state) => state.app.filter
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const routerState = useSelector<AppState, RouterState>(
    (state) => state.app.router
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const principalState = useSelector<AppState, Principal>(
    (state) => state.app.principal
  );

  // プラン一覧データの取得状況
  const isProcessingPlanList =
    fetchState[GET_PLAN_LIST_ID] !== null &&
    fetchState[GET_PLAN_LIST_ID] === FetchStatus.PROCESSING;
  // 購入情報一覧の取得状況
  const isProcessingPurchasePlanList =
    fetchState[GET_PURCHASE_HISTORY_ID] !== undefined &&
    (fetchState[GET_PURCHASE_HISTORY_ID] === FetchStatus.PROCESSING ||
      !principalState.isLoggedIn);

  return {
    planValue: filterState.planValue,
    modelValue: filterState.modelValue,
    modelOptions: filterState.modelOptions,
    isProcessingList: isProcessingPlanList || isProcessingPurchasePlanList,
    glossary: Glossary.instance(glState),
    router: routerState
  };
};

type SelectedState = ReturnType<typeof useStateProps>;

/** StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ*/
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const query = new URLSearchParams(state.router.location.search);
  const _pathname = state.router.location.pathname;

  // URLを表示している画面に応じて変更する
  const changeUrl = () => {
    if (_pathname === '/store/plan') {
      // 表示されている画面がプラン一覧画面の場合
      dispatch(push(getPlanListUrl()));
    } else if (_pathname === '/store/purchase') {
      // 表示されている画面が利用中一覧画面の場合
      dispatch(push(getPurchaseUrl()));
    }
  };

  // プランのセレクトボックスの値を変更する
  const onChangePlan = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    // value(''か'all'か'app')をPlanOptionValueの形に変換する
    let planTypeValue: PlanOptionValue;
    if (value === '') {
      planTypeValue = PlanOptionValue.ALL_PLAN;
    } else if (value === 'app') {
      planTypeValue = PlanOptionValue.APP_SERVICE;
    } else {
      planTypeValue = PlanOptionValue.CAMERA_PAID_FEATURES;
    }

    // プランのセレクトボックスの値を変更する
    dispatch(setPlanValue(planTypeValue));

    // 機種のセレクトボックスの値を変更する
    // 「すべてのプラン」を選択した場合は機種のセレクトボックスは「-」と表示する
    // 「カメラ有償機能」を選択した場合は機種のセレクトボックスは「すべての機種」と表示する
    // 「アプリ/サービス」を選択した場合は機種のセレクトボックスは「-」と表示する(Filterコンポーネントの方でappの場合も「-」と表示するようにしている)
    dispatch(setModelValue(planTypeValue));

    // cookieに保存&クリア
    if (planTypeValue === PlanOptionValue.APP_SERVICE) {
      saveCookie(SasCookieKeys.KEY_TYPE, planTypeValue);
      saveCookie(SasCookieKeys.KEY_MODEL_NAME, '');
    } else {
      saveCookie(SasCookieKeys.KEY_TYPE, '');
      saveCookie(SasCookieKeys.KEY_MODEL_NAME, planTypeValue);
    }

    // urlを変更する
    changeUrl();

    // 一覧を更新する
    // 「すべてのプラン」を選択した場合はすべてのプランを表示する(=初期値)
    // 「カメラ有償機能」を選択した場合はカメラ有償機能のみに絞り込む
    // 「アプリ/サービス」を選択した場合はカメラ有償機能以外のみに絞り込む
    if (_pathname === '/store/plan') {
      if (planTypeValue === PlanOptionValue.APP_SERVICE) {
        dispatch(planFilterState('', planTypeValue));
      } else {
        dispatch(planFilterState(planTypeValue, ''));
      }
    } else if (_pathname === '/store/purchase') {
      if (planTypeValue === PlanOptionValue.APP_SERVICE) {
        dispatch(purchaseFilterState('', planTypeValue));
      } else {
        dispatch(purchaseFilterState(planTypeValue, ''));
      }
    }
  };

  // 機種のセレクトボックスの値を変更する
  const onChangeModel = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // 機種の絞り込み時にはプランのセレクトボックスは必ずカメラ有償機能の状態だからplanValueを変更する必要はない
    const value = e.target.value;
    // 機種のセレクトボックスの値を変更する
    dispatch(setModelValue(value));
    // cookieに保存
    saveCookie(SasCookieKeys.KEY_MODEL_NAME, value);
    // urlを変更する
    changeUrl();
    // 一覧を更新する
    // 機種のセレクトボックスの操作はカメラ有償機能選択時のみ可能になるためtypeの値は常に存在しない
    if (_pathname === '/store/plan') {
      dispatch(planFilterState(value, ''));
    } else if (_pathname === '/store/purchase') {
      dispatch(purchaseFilterState(value, ''));
    }
  };

  // クリアボタン押下時の処理
  const onClickFilterClear = () => {
    // planValueとmodelValueを初期値に戻す
    dispatch(setPlanValue(PlanOptionValue.ALL_PLAN));
    dispatch(setModelValue(''));
    // cookieのmodelNameとtypeの値をクリアする
    saveCookie(SasCookieKeys.KEY_MODEL_NAME, '');
    saveCookie(SasCookieKeys.KEY_TYPE, '');
    //  urlを変更する
    changeUrl();
    // 一覧を更新する
    if (_pathname === '/store/plan') {
      // plansを更新する(初期状態に戻す)
      dispatch(planFilterState('', ''));
    } else if (_pathname === '/store/purchase') {
      // contractPlansを更新する(初期状態に戻す)
      dispatch(purchaseFilterState('', ''));
    }
  };

  return {
    query,
    onChangePlan,
    onChangeModel,
    onClickFilterClear
  };
};

export default function ContainerComponent() {
  const _props = { ...useStateProps(), ...useMargeProps(useStateProps()) };
  return <Filter {...{ ..._props }} />;
}
